import Type from "../Types";
import { showToast } from "../Utility";

export function diffGeoJSON(oldGeoJSON, newGeoJSON, opts = {}) {
  const options = {
    tolerateLineReverse: true, // Treat reversed lines as equal
    ...opts,
  };

  const seenRefIds = new Set();
  const nextRefId = () => {
    // 10-digit ref_id
    let id;
    do {
      id = String(Math.floor(1e9 + Math.random() * 9e9));
    } while (seenRefIds.has(id));
    seenRefIds.add(id);
    return parseInt(id);
  };

  // Deep equality
  const deepEqual = (a, b) => {
    if (a === b) return true;
    if (Number.isNaN(a) && Number.isNaN(b)) return true;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    if (a && b && typeof a === "object") {
      const aKeys = Object.keys(a).sort();
      const bKeys = Object.keys(b).sort();
      if (aKeys.length !== bKeys.length) return false;
      for (let i = 0; i < aKeys.length; i++) {
        if (aKeys[i] !== bKeys[i]) return false;
      }
      for (const k of aKeys) {
        if (!deepEqual(a[k], b[k])) return false;
      }
      return true;
    }

    return false;
  };

  // Helpers
  const arrEq = (a, b) => deepEqual(a, b);

  const stripClosingDuplicate = (ring) => {
    if (!Array.isArray(ring) || ring.length < 2) return ring;
    const first = ring[0];
    const last = ring[ring.length - 1];
    return arrEq(first, last) ? ring.slice(0, ring.length - 1) : ring;
  };

  const cmpPos = (a, b) => {
    const ax = a?.[0] ?? 0;
    const ay = a?.[1] ?? 0;
    const az = a?.[2] ?? 0;
    const bx = b?.[0] ?? 0;
    const by = b?.[1] ?? 0;
    const bz = b?.[2] ?? 0;
    return ax - bx || ay - by || az - bz;
  };

  const rotate = (arr, i) => arr.slice(i).concat(arr.slice(0, i));

  const minRotation = (seq) => {
    let minIdx = 0;
    for (let i = 1; i < seq.length; i++) {
      if (cmpPos(seq[i], seq[minIdx]) < 0) minIdx = i;
    }
    return rotate(seq, minIdx);
  };

  const canonRing = (ring) => {
    const r = stripClosingDuplicate(ring);
    if (!Array.isArray(r) || r.length < 3) return r;
    const a = minRotation(r);
    const b = minRotation([...r].reverse());
    for (let i = 0; i < a.length; i++) {
      const c = cmpPos(a[i], b[i]);
      if (c < 0) return a;
      if (c > 0) return b;
    }
    return a;
  };

  const canonLine = (line) => {
    if (!Array.isArray(line) || line.length < 2) return line;
    if (!options.tolerateLineReverse) return line;
    const a = line;
    const b = [...line].reverse();
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
      const c = cmpPos(a[i], b[i]);
      if (c < 0) return a;
      if (c > 0) return b;
    }
    return a;
  };

  // Keep only normalized coordinates
  const canonCoords = (geom) => {
    if (!geom) return null;

    switch (geom.type) {
      case "Point":
        return geom.coordinates || null;
      case "MultiPoint":
        return (geom.coordinates || []).slice().sort(cmpPos);
      case "LineString":
        return canonLine(geom.coordinates || []);
      case "MultiLineString":
        return (geom.coordinates || []).map(canonLine);
      case "Polygon":
        return (geom.coordinates || []).map(canonRing);
      case "MultiPolygon":
        return (geom.coordinates || []).map((p) => (p || []).map(canonRing));
      case "GeometryCollection":
        return (geom.geometries || []).map(canonCoords);
      default:
        return null;
    }
  };

  const normFC = (fc) => {
    const features = (fc?.features || []).map((f) => ({
      ...f,
      geometry: canonCoords(f.geometry),
    }));
    return { ...(fc || { type: "FeatureCollection" }), features };
  };

  // ID helpers
  const getId = (f) => {
    const pid = f?.properties?.id;
    if (pid !== undefined && pid !== null && pid !== "") return String(pid);
    const fid = f?.id;
    if (fid !== undefined && fid !== null && fid !== "") return String(fid);
    return null;
  };

  const rawId = (f, fallback) => f?.properties?.id ?? f?.id ?? fallback;

  const makeMissingIdSignature = (feature) => {
    return JSON.stringify({
      geometry: feature?.geometry ?? null,
      properties: feature?.properties ?? {},
    });
  };

  const makeFallbackKey = (feature, index, side) => {
    return `__noid__:${side}:${index}:${makeMissingIdSignature(feature)}`;
  };

  const indexFeatures = (features, side, missingIdSignatures) => {
    const map = new Map();

    for (let i = 0; i < (features || []).length; i++) {
      const f = features[i];
      const id = getId(f);

      if (id !== null) {
        let key = id;
        let duplicateIndex = 1;

        while (map.has(key)) {
          key = `${id}__dup__${duplicateIndex++}`;
        }

        map.set(key, f);
        continue;
      }

      missingIdSignatures.add(makeMissingIdSignature(f));
      const fallbackKey = makeFallbackKey(f, i, side);
      map.set(fallbackKey, f);
    }

    return map;
  };

  const A = normFC(oldGeoJSON || {});
  const B = normFC(newGeoJSON || {});

  const missingIdSignatures = new Set();

  const aIdx = indexFeatures(A.features, "old", missingIdSignatures);
  const bIdx = indexFeatures(B.features, "new", missingIdSignatures);

  const changes = [];

  // Add
  for (const [id, f] of bIdx.entries()) {
    if (!aIdx.has(id)) {
      const cleanProps = { ...(f.properties ?? {}) };

      if (cleanProps.id == null && !String(id).startsWith("__noid__")) {
        cleanProps.id = rawId(f, id);
      }

      const ref_id = nextRefId();

      changes.push({
        type: "add",
        ref_id,
        geometry: f.geometry ?? null,
        properties: cleanProps,
      });
    }
  }

  // Delete
  for (const [id, f] of aIdx.entries()) {
    if (!bIdx.has(id)) {
      const feature_id = rawId(f, id);
      changes.push({ type: "delete", feature_id });
    }
  }

  // Modify
  for (const [id, nf] of bIdx.entries()) {
    if (!aIdx.has(id)) continue;

    const of = aIdx.get(id);
    const geometryChanged = !deepEqual(of.geometry, nf.geometry);
    const propertiesChanged = !deepEqual(of.properties, nf.properties);

    if (geometryChanged || propertiesChanged) {
      const change = {
        type: "modify",
        feature_id: rawId(nf, id),
      };

      if (geometryChanged) change.geometry = nf.geometry ?? null;
      if (propertiesChanged) change.properties = nf.properties ?? {};

      changes.push(change);
    }
  }

  if (missingIdSignatures.size > 0) {
    const message = `${missingIdSignatures.size} feature(s) do not have an id.`;
    showToast(message, Type.NOTIFICATION.WARNING, { duration: 5000 });
  }

  return changes;
}